import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { changeDateRange } from 'features/date-range-selection/dateRangeSlice';
import {
  applySearchToGraph,
  applySearchToTable,
  applySearchToTableAndGraph,
  clearSearch
} from 'features/search-bar/searchBarSlice';
import { selectSite } from 'features/topbar/selectedSiteSlice';
import { WritableDraft } from 'immer/dist/internal';
import { PURGE } from 'redux-persist';

const MAX_PAGE_SIZE = 1000;

export interface PreviousRequest {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
  forwardSeekValue?: string;
  forwardSeekId?: string;
  backwardSeekValue?: string;
  backwardSeekId?: string;
  seekColumnName?: string;
  orderDirection?: string;
}

export interface PaginationOptionsState {
  componentId?: string;
  pageSize: number;
  currentPage: number;
  searchTerm?: string;
  seekValue?: string;
  seekColumnName?: string;
  defaultColumnName?: string;
  seekId?: string;
  seekDirection?: string;
  orderDirection?: string;
  previousRequest?: PreviousRequest;
}

const defaultPaginationOptions: PaginationOptionsState = {
  pageSize: 10,
  currentPage: 1,
  seekValue: undefined,
  seekColumnName: undefined,
  seekId: undefined,
  defaultColumnName: undefined,
  seekDirection: undefined,
  previousRequest: undefined,
  orderDirection: undefined,
  searchTerm: undefined
};

const paginationOptionsSlice = createSlice({
  name: 'paginationOptions',
  initialState: {
    paginationOptions: [] as PaginationOptionsState[]
  },
  reducers: {
    resetToFirstPage: (
      state,
      { payload: { componentId } }: PayloadAction<{ componentId: string }>
    ) => {
      applyResetToFirstPage(state, componentId);
    },
    initializePaginationOptions: (
      state,
      {
        payload: { componentId, defaultColumnName, orderDirection }
      }: PayloadAction<{
        componentId: string;
        defaultColumnName: string;
        orderDirection: string;
      }>
    ) => {
      if (!state.paginationOptions.find((x) => x.componentId === componentId)) {
        const initializedPaginationOptions = {
          ...defaultPaginationOptions,
          componentId
        };

        initializedPaginationOptions.defaultColumnName = defaultColumnName;
        initializedPaginationOptions.seekColumnName = defaultColumnName;
        initializedPaginationOptions.orderDirection = orderDirection;
        state.paginationOptions.push(initializedPaginationOptions);
      }
    },
    setPaginationOptions: (
      state,
      {
        payload: { componentId, paginationOptions }
      }: PayloadAction<{
        componentId: string;
        paginationOptions: PaginationOptionsState;
      }>
    ) => {
      const targetPaginationOptions = state.paginationOptions.find(
        (options: PaginationOptionsState) => options.componentId === componentId
      );

      if (targetPaginationOptions) {
        Object.assign(targetPaginationOptions, paginationOptions);
      } else {
        state.paginationOptions.push({
          ...defaultPaginationOptions,
          componentId
        });
      }
    },
    setPreviousRequest: (
      state,
      {
        payload: { componentId, previousRequest }
      }: PayloadAction<{
        componentId: string;
        previousRequest: PreviousRequest;
      }>
    ) => {
      const targetPaginationOptions = state.paginationOptions.find(
        (options: PaginationOptionsState) => options.componentId === componentId
      );

      if (targetPaginationOptions) {
        targetPaginationOptions.previousRequest = previousRequest;
      }
    },
    setPageSize: (
      state,
      {
        payload: { componentId, pageSize }
      }: PayloadAction<{ componentId: string; pageSize: number }>
    ) => {
      let targetPaginationOptions = state.paginationOptions.find(
        (options: PaginationOptionsState) => options.componentId === componentId
      );

      if (!targetPaginationOptions) {
        targetPaginationOptions = { ...defaultPaginationOptions, componentId };
      }

      applyResetToFirstPage(state, componentId);

      pageSize = pageSize < MAX_PAGE_SIZE ? pageSize : MAX_PAGE_SIZE;
      targetPaginationOptions.pageSize = pageSize;
    },
    setColumnSorting: (
      state,
      {
        payload: { componentId, columnName, orderDirection }
      }: PayloadAction<{
        componentId: string;
        columnName: string;
        orderDirection: string;
      }>
    ) => {
      let targetPaginationOptions = state.paginationOptions.find(
        (options: PaginationOptionsState) => options.componentId === componentId
      );

      if (!targetPaginationOptions) {
        targetPaginationOptions = { ...defaultPaginationOptions, componentId };
      } else {
        targetPaginationOptions.seekId = undefined;
        targetPaginationOptions.seekValue = undefined;
        targetPaginationOptions.currentPage = 1;
        targetPaginationOptions.previousRequest = undefined;
      }

      targetPaginationOptions.orderDirection = orderDirection;
      targetPaginationOptions.seekColumnName = columnName;
    },
    nextPage: (
      state,
      { payload: { componentId } }: PayloadAction<{ componentId: string }>
    ) => {
      let targetPaginationOptions = state.paginationOptions.find(
        (options: PaginationOptionsState) => options.componentId === componentId
      );

      if (!targetPaginationOptions) {
        targetPaginationOptions = { ...defaultPaginationOptions, componentId };
      }

      if (!targetPaginationOptions.previousRequest) {
        return;
      }
      if (
        targetPaginationOptions.previousRequest?.totalPages >
        targetPaginationOptions.currentPage
      ) {
        if (
          targetPaginationOptions.previousRequest?.currentPage ===
          targetPaginationOptions.currentPage
        ) {
          targetPaginationOptions.seekValue =
            targetPaginationOptions.previousRequest?.forwardSeekValue;
          targetPaginationOptions.seekId =
            targetPaginationOptions.previousRequest?.forwardSeekId;
          targetPaginationOptions.seekDirection = 'pageForward';

          targetPaginationOptions.currentPage++;
        }
      }
    },
    previousPage: (
      state,
      { payload: { componentId } }: PayloadAction<{ componentId: string }>
    ) => {
      let targetPaginationOptions = state.paginationOptions.find(
        (options: PaginationOptionsState) => options.componentId === componentId
      );

      if (!targetPaginationOptions) {
        targetPaginationOptions = { ...defaultPaginationOptions, componentId };
      }

      if (targetPaginationOptions.currentPage > 1) {
        if (!targetPaginationOptions.previousRequest) {
          targetPaginationOptions.currentPage = 1;
        } else {
          targetPaginationOptions.seekValue =
            targetPaginationOptions.previousRequest.backwardSeekValue;
          targetPaginationOptions.seekId =
            targetPaginationOptions.previousRequest.backwardSeekId;
          targetPaginationOptions.seekDirection = 'pageBackward';

          targetPaginationOptions.currentPage -= 1;
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(changeDateRange, (state, action) => {
      state.paginationOptions.forEach((options) => {
        options.currentPage = 1;
        options.seekValue = undefined;
        options.seekId = undefined;
        options.seekDirection = undefined;
        options.previousRequest = undefined;
      });
    });
    builder.addCase(selectSite, (state, action) => {
      state.paginationOptions.forEach((options) => {
        options.currentPage = 1;
        options.seekValue = undefined;
        options.seekId = undefined;
        options.seekDirection = undefined;
        options.previousRequest = undefined;
      });
    });
    builder.addCase(applySearchToTable, (state, action) => {
      updateSearchTermState(state, action.payload);
    });
    builder.addCase(applySearchToTableAndGraph, (state, action) => {
      updateSearchTermState(state, action.payload);
    });
    builder.addCase(applySearchToGraph, (state, action) => {
      updateSearchTermState(state, action.payload);
    });
    builder.addCase(clearSearch, (state, action) => {
      const payload = {
        id: action.payload.id,
        valueToSearch: ''
      };
      updateSearchTermState(state, payload);
    });
    builder.addCase(PURGE, () => ({
      paginationOptions: [] as PaginationOptionsState[]
    }));
  }
});

const applyResetToFirstPage = (
  state: WritableDraft<{
    paginationOptions: PaginationOptionsState[];
  }>,
  componentId: string
) => {
  const paginationOptions = state.paginationOptions.find(
    (x) => x.componentId === componentId
  );
  if (paginationOptions) {
    paginationOptions.currentPage = 1;
    paginationOptions.seekValue = undefined;
    paginationOptions.seekId = undefined;
    paginationOptions.seekDirection = undefined;
    paginationOptions.previousRequest = undefined;
  }
};

const updateSearchTermState = (
  state: WritableDraft<{ paginationOptions: PaginationOptionsState[] }>,
  payload: { id: string; valueToSearch: string }
) => {
  const targetPaginationOptions = state.paginationOptions.find(
    (options: PaginationOptionsState) => options.componentId === payload.id
  );

  if (targetPaginationOptions) {
    targetPaginationOptions.searchTerm = payload.valueToSearch;
  }
};

const selectSelf = (state: RootState) => state.paginationOptions;
const selectPaginationOptionsByComponentIdId = (
  state: RootState,
  componentId: string
) => componentId;

export const selectPaginationOptions = createSelector(
  selectSelf,
  selectPaginationOptionsByComponentIdId,
  (state, componentId) => {
    const paginationOptions = state.paginationOptions.find(
      (options: PaginationOptionsState) => options.componentId === componentId
    );

    return {
      pageSize:
        paginationOptions?.pageSize ?? defaultPaginationOptions.pageSize,
      currentPage:
        paginationOptions?.currentPage ?? defaultPaginationOptions.currentPage,
      totalPages: paginationOptions?.previousRequest?.totalPages ?? 0,
      totalRecords: paginationOptions?.previousRequest?.totalRecords ?? 0,
      searchTerm: paginationOptions?.searchTerm,
      seekValue: paginationOptions?.seekValue,
      seekId: paginationOptions?.seekId,
      seekDirection: paginationOptions?.seekDirection,
      seekColumnName: paginationOptions?.seekColumnName,
      orderDirection: paginationOptions?.orderDirection
    };
  }
);

export const selectNullablePaginationOptions = createSelector(
  selectSelf,
  selectPaginationOptionsByComponentIdId,
  (state, componentId) => {
    const paginationOptions = state.paginationOptions.find(
      (options: PaginationOptionsState) => options.componentId === componentId
    );

    if (!paginationOptions) {
      return;
    }

    return {
      pageSize:
        paginationOptions?.pageSize ?? defaultPaginationOptions.pageSize,
      currentPage:
        paginationOptions?.currentPage ?? defaultPaginationOptions.currentPage,
      totalPages: paginationOptions?.previousRequest?.totalPages ?? 0,
      totalRecords: paginationOptions?.previousRequest?.totalRecords ?? 0,
      searchTerm: paginationOptions?.searchTerm,
      seekValue: paginationOptions?.seekValue,
      seekId: paginationOptions?.seekId,
      seekDirection: paginationOptions?.seekDirection,
      seekColumnName: paginationOptions?.seekColumnName,
      orderDirection: paginationOptions?.orderDirection
    };
  }
);

export const selectLastPaginationRequest = createSelector(
  selectSelf,
  selectPaginationOptionsByComponentIdId,
  (state, componentId) => {
    const paginationOptions = state.paginationOptions.find(
      (options: PaginationOptionsState) => options.componentId === componentId
    );

    return { ...paginationOptions?.previousRequest };
  }
);

const { actions, reducer } = paginationOptionsSlice;

export const {
  initializePaginationOptions,
  setPaginationOptions,
  nextPage,
  previousPage,
  setPageSize,
  setPreviousRequest,
  resetToFirstPage,
  setColumnSorting
} = actions;

export default reducer;
